import type { DonationFormState, FormAction, FormData, FormStep } from '../types';
import { hasPaypalApproval, prefillPaypalDonor } from '../paypalCheckout';
import type { DonationFrequency } from '@kuow/core';
import { tiersFromCurrentAmount } from '@kuow/core';

import {
  validateAmountStep,
  validateGiftStep,
  validatePaymentStep,
  validateInformationStep,
} from '../validation';

const roundTwoPlaces = (value: number): number => {
  return Math.round(value * 100) / 100;
};

const calculateCoverCostAmount = (amount: number): number => {
  return roundTwoPlaces(amount * 0.0166 + 0.6);
};

interface DonationAmounts {
  coverTheCostAmount: number;
  totalDonationAmount: number | null;
}

const calculateDonationAmounts = (
  amount: number | null,
  customAmount: number | null,
  coverTheCost: boolean,
): DonationAmounts => {
  const baseAmount = customAmount ?? amount;
  
  if (baseAmount === null || baseAmount <= 0) {
    return {
      coverTheCostAmount: 0,
      totalDonationAmount: null,
    };
  }

  // Always calculate the cover cost fee for display purposes
  const coverCostFee = calculateCoverCostAmount(baseAmount);
  // Only add the fee to the total if the option is enabled
  const total = coverTheCost ? roundTwoPlaces(baseAmount + coverCostFee) : baseAmount;

  return {
    coverTheCostAmount: coverCostFee,
    totalDonationAmount: total,
  };
};

const getFirstTierAmount = (
  formDetails: DonationFormState['formDetails'],
  frequency: DonationFrequency,
): number | null => {
  if (!formDetails || formDetails.donation_tiers.length === 0) {
    return null;
  }

  const firstTier = formDetails.donation_tiers[0];
  const rawAmount =
    frequency === 'monthly'
      ? firstTier.monthly_donation_amount
      : firstTier.single_donation_amount;

  const numericAmount = parseFloat(rawAmount);

  if (Number.isNaN(numericAmount) || numericAmount <= 0) {
    return null;
  }

  return numericAmount;
};

const getHighestOneTimeTierAmount = (formDetails: DonationFormState['formDetails']): number | null => {
  if (!formDetails || formDetails.donation_tiers.length === 0) {
    return null;
  }

  const values = formDetails.donation_tiers
    .map((tier) => parseFloat(tier.single_donation_amount))
    .filter((amount) => Number.isFinite(amount) && amount > 0);

  if (values.length === 0) return null;
  return Math.max(...values);
};

const shouldShowMonthlyUpsell = (state: DonationFormState): boolean => {
  if (!state.formDetails?.evergreen_upsell) return false;
  if (state.formData.frequency !== 'one-time') return false;

  const highestTier = getHighestOneTimeTierAmount(state.formDetails);
  if (highestTier === null) return true;

  const selectedAmount = state.formData.customAmount ?? state.formData.amount;
  if (selectedAmount === null) return true;

  return selectedAmount < highestTier;
};

const deriveNextBaseAmount = (state: DonationFormState): number | null =>
  state.currentStep === 'amount'
    ? state.formData.customAmount ?? state.formData.amount ?? 0
    : state.baseAmount;

const deriveNextBaseAmountIsCustom = (state: DonationFormState): boolean =>
  state.currentStep === 'amount'
    ? state.formData.customAmount !== null
    : state.baseAmountIsCustom;

/**
 * Returns `{amount, customAmount}` that reflect the donor's committed base
 * amount, routed to the same field type they originally used (tier vs
 * custom). Falls back to the current formData when no base has been
 * captured yet.
 */
const baseToFormFields = (
  state: DonationFormState,
): { amount: number | null; customAmount: number | null } => {
  if (state.baseAmount === null) {
    return { amount: state.formData.amount, customAmount: state.formData.customAmount };
  }
  return state.baseAmountIsCustom
    ? { amount: null, customAmount: state.baseAmount }
    : { amount: state.baseAmount, customAmount: null };
};

const preserveCurrentOrBaseFormFields = (
  state: DonationFormState,
): { amount: number | null; customAmount: number | null } => {
  const base = baseToFormFields(state);
  const baseValue = base.customAmount ?? base.amount;
  const currentValue = state.formData.customAmount ?? state.formData.amount;

  if (baseValue === null || currentValue === null) {
    return base;
  }

  const target = Math.max(baseValue, currentValue);
  return base.customAmount !== null
    ? { amount: null, customAmount: target }
    : { amount: target, customAmount: null };
};

/**
 * Returns `{amount, customAmount}` = `max(base, current, selectedGift.minPrice)`
 * routed to the donor's original field type (tier vs custom). With no
 * selected gift or no matching gift in formDetails, returns base unchanged.
 */
const giftAdjustedFormFields = (
  state: DonationFormState,
  giftSelection: FormData['gift'],
): { amount: number | null; customAmount: number | null } => {
  const base = baseToFormFields(state);
  const baseValue = base.customAmount ?? base.amount;
  if (baseValue === null || !giftSelection || !state.formDetails) {
    return base;
  }
  const gift = state.formDetails.gifts.find((g) => g.id === giftSelection.giftId);
  if (!gift) {
    return base;
  }
  const priceField = state.formData.frequency === 'monthly' ? 'price_per_monthly' : 'price_per_single';
  const giftPrice = parseFloat(gift[priceField]);
  if (!Number.isFinite(giftPrice)) {
    return base;
  }
  const currentValue = state.formData.customAmount ?? state.formData.amount ?? 0;
  const target = Math.max(baseValue, currentValue, giftPrice);
  const isCustom = base.customAmount !== null;
  return isCustom
    ? { amount: null, customAmount: target }
    : { amount: target, customAmount: null };
};

/**
 * Price of the currently selected gift for the given frequency (defaults to
 * the active one), or null when no gift is selected / the price can't be
 * resolved.
 */
const selectedGiftPrice = (
  state: DonationFormState,
  frequency: DonationFrequency = state.formData.frequency,
): number | null => {
  const giftSelection = state.formData.gift;
  if (!giftSelection || !state.formDetails) {
    return null;
  }
  const gift = state.formDetails.gifts.find((g) => g.id === giftSelection.giftId);
  if (!gift) {
    return null;
  }
  const priceField =
    frequency === 'monthly' ? 'price_per_monthly' : 'price_per_single';
  const price = parseFloat(gift[priceField]);
  return Number.isFinite(price) ? price : null;
};

/**
 * When the donor's committed amount drops below the selected gift's price —
 * whether by explicitly lowering the amount or by switching to a frequency
 * where the gift costs more — the gift can no longer be afforded. Drop it so
 * the donor's chosen amount is respected and the "+ $N" premium resets. Gifts
 * whose price is within the new amount stay selected.
 */
const giftAfterAmountChange = (
  state: DonationFormState,
  newAmount: number | null,
  frequency: DonationFrequency = state.formData.frequency,
): Pick<FormData, 'gift' | 'noGift'> => {
  const price = selectedGiftPrice(state, frequency);
  if (price !== null && (newAmount ?? 0) < price) {
    return { gift: null, noGift: true };
  }
  return { gift: state.formData.gift, noGift: state.formData.noGift };
};

/**
 * Given a target step, return the `{amount, customAmount}` the formData
 * should reflect on that step: base amount when landing on AmountStep,
 * gift-adjusted base elsewhere.
 */
const reconcileAmountForStep = (
  state: DonationFormState,
  nextStepId: FormStep,
): { amount: number | null; customAmount: number | null } => {
  if (nextStepId === 'amount') {
    return baseToFormFields(state);
  }
  return giftAdjustedFormFields(state, state.formData.gift);
};

/**
 * Applies a step transition: captures baseAmount on exit from AmountStep,
 * then reconciles formData.amount/customAmount for the target step so the
 * donor sees their committed base on AmountStep and the gift-adjusted
 * amount everywhere else.
 */
const applyStepTransition = (
  state: DonationFormState,
  nextStepId: FormStep,
): DonationFormState => {
  const stateWithBase: DonationFormState = {
    ...state,
    baseAmount: deriveNextBaseAmount(state),
    baseAmountIsCustom: deriveNextBaseAmountIsCustom(state),
  };

  // Leaving the amount step re-commits the donor's base amount. If a selected
  // gift now costs more than that base, it can no longer be afforded — drop it
  // (mirroring giftAfterAmountChange on explicit amount edits) so the
  // gift-adjusted reconciliation below doesn't silently re-inflate the
  // donation back up to the gift's price. Without this, returning to the
  // amount step at a lower base and advancing again would snap the total back
  // to the gift price even though the donor never re-confirmed it.
  const giftFields =
    state.currentStep === 'amount'
      ? giftAfterAmountChange(stateWithBase, stateWithBase.baseAmount)
      : { gift: stateWithBase.formData.gift, noGift: stateWithBase.formData.noGift };
  const giftCleared = giftFields.gift === null && stateWithBase.formData.gift !== null;
  const reconcileFromState: DonationFormState = {
    ...stateWithBase,
    formData: { ...stateWithBase.formData, ...giftFields },
  };

  const { amount, customAmount } = reconcileAmountForStep(reconcileFromState, nextStepId);
  const amounts = calculateDonationAmounts(amount, customAmount, reconcileFromState.formData.coverTheCost);
  const newFormData: FormData = {
    ...reconcileFromState.formData,
    amount,
    customAmount,
    coverTheCostAmount: amounts.coverTheCostAmount,
    totalDonationAmount: amounts.totalDonationAmount,
  };
  const requiresEmail = state.formDetails?.kind !== 'call_center';
  const validationOptions = {
    disableCaptcha: state.disableCaptcha,
    alwaysShowAddress: state.formDetails?.always_show_address ?? false,
  };
  return {
    ...reconcileFromState,
    currentStep: nextStepId,
    formData: newFormData,
    validation: {
      ...reconcileFromState.validation,
      amount: validateAmountStep(newFormData),
      // Dropping the gift changes whether a gift is required and whether
      // shipping fields are needed, so re-validate those steps too.
      ...(giftCleared
        ? {
            gift: validateGiftStep(newFormData, state.formDetails),
            information: validateInformationStep(newFormData, requiresEmail, validationOptions),
          }
        : {}),
    },
  };
};

function reduceForm(state: DonationFormState, action: FormAction): DonationFormState {
  switch (action.type) {
    case 'SET_FORM_DETAILS': {
      const frequency: DonationFrequency =
        state.mode === 'monthly-upgrade' || state.mode === 'billing-update'
          ? 'monthly'
          : action.payload.default_monthly
          ? 'monthly'
          : 'one-time';

      const formDetailsWithOverride =
        state.mode === 'monthly-upgrade' && state.activeSubscription
          ? (() => {
              const override = tiersFromCurrentAmount(state.activeSubscription.amount);
              return override ? { ...action.payload, donation_tiers: override } : action.payload;
            })()
          : action.payload;

      const defaultAmount = getFirstTierAmount(formDetailsWithOverride, frequency);
      const coverTheCost = false;
      const amounts = calculateDonationAmounts(defaultAmount, null, coverTheCost);
      const newFormData = {
        ...state.formData,
        frequency,
        amount: defaultAmount,
        customAmount: null,
        coverTheCost,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
        gift: null,
        noGift: action.payload.gifts.length > 0,
      };

      return {
        ...state,
        formDetails: formDetailsWithOverride,
        isLoading: state.mode === 'monthly-upgrade' ? state.isLoading : false,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, formDetailsWithOverride),
        },
      };
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'SET_SUBMISSION_ERROR':
      return {
        ...state,
        submissionError: action.payload,
      };

    case 'SET_ACTIVE_SUBSCRIPTION': {
      const nextActiveSubscription = action.payload;

      const nextFormDetails =
        state.mode === 'monthly-upgrade' && state.formDetails
          ? (() => {
              const override = tiersFromCurrentAmount(nextActiveSubscription.amount);
              return override ? { ...state.formDetails, donation_tiers: override } : state.formDetails;
            })()
          : state.formDetails;

      const shouldResetAmount =
        state.mode === 'monthly-upgrade' &&
        state.currentStep === 'amount' &&
        state.formData.customAmount === null;

      const nextDefaultAmount = shouldResetAmount
        ? getFirstTierAmount(nextFormDetails, 'monthly')
        : state.formData.amount;

      const amounts = calculateDonationAmounts(nextDefaultAmount, null, state.formData.coverTheCost);

      const nextFormData = shouldResetAmount
        ? {
            ...state.formData,
            frequency: 'monthly' as const,
            amount: nextDefaultAmount,
            customAmount: null,
            coverTheCostAmount: amounts.coverTheCostAmount,
            totalDonationAmount: amounts.totalDonationAmount,
          }
        : state.formData;

      return {
        ...state,
        activeSubscription: nextActiveSubscription,
        formDetails: nextFormDetails,
        useNewPaymentMethod: nextActiveSubscription.payment_method?.account_type === 'paypal' || state.useNewPaymentMethod,
        formData: { ...nextFormData, upgradeDonationId: nextActiveSubscription.id, paypalApproval: null, paypalCompletionUncertain: false },
        validation: shouldResetAmount
          ? {
              ...state.validation,
              amount: validateAmountStep(nextFormData),
            }
          : state.validation,
        isLoading: state.formDetails ? false : state.isLoading,
      };
    }

    case 'SET_USE_NEW_PAYMENT_METHOD':
      return {
        ...state,
        useNewPaymentMethod: action.payload,
      };

    case 'SET_BILLING_DONATION': {
      const { donation, updateToken, withAuth, initialStep } = action.payload;
      const existingAmount = typeof donation.amount === 'string' ? parseFloat(donation.amount) : donation.amount;
      const amounts = calculateDonationAmounts(null, existingAmount, state.formData.coverTheCost);

      const nextFormData = {
        ...state.formData,
        frequency: 'monthly' as const,
        billingDonationId: donation.id,
        billDate: donation.next_bill_date ?? donation.bill_date ?? new Date().toISOString(),
        paypalApproval: null,
        paypalCompletionUncertain: false,
        amount: null,
        customAmount: existingAmount,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
        gift: null,
        noGift: true,
      };

      return {
        ...state,
        activeSubscription: donation,
        billingUpdateToken: updateToken,
        billingUpdateWithAuth: withAuth,
        formDetails: donation.form,
        currentStep: initialStep ?? 'payment',
        formData: nextFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(nextFormData),
          gift: true,
          information: true,
        },
        isLoading: false,
        error: null,
      };
    }

    case 'SET_FREQUENCY': {
      const frequency = action.payload;
      const isFrequencyChanged = state.formData.frequency !== frequency;
      const amount = isFrequencyChanged
        ? getFirstTierAmount(state.formDetails, frequency)
        : state.formData.amount;
      const customAmount = isFrequencyChanged ? null : state.formData.customAmount;
      const amounts = calculateDonationAmounts(amount, customAmount, state.formData.coverTheCost);
      const { gift, noGift } = giftAfterAmountChange(state, customAmount ?? amount, frequency);

      const newFormData = {
        ...state.formData,
        frequency,
        amount,
        customAmount,
        gift,
        noGift,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, state.formDetails),
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'SET_AMOUNT': {
      const amounts = calculateDonationAmounts(action.payload, null, state.formData.coverTheCost);
      const { gift, noGift } = giftAfterAmountChange(state, action.payload);
      const newFormData = {
        ...state.formData,
        amount: action.payload,
        customAmount: null,
        gift,
        noGift,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, state.formDetails),
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'SET_CUSTOM_AMOUNT': {
      const amounts = calculateDonationAmounts(null, action.payload, state.formData.coverTheCost);
      const { gift, noGift } = giftAfterAmountChange(state, action.payload);
      const newFormData = {
        ...state.formData,
        customAmount: action.payload,
        amount: null,
        gift,
        noGift,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, state.formDetails),
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'SET_COVER_THE_COST': {
      const amounts = calculateDonationAmounts(
        state.formData.amount,
        state.formData.customAmount,
        action.payload,
      );
      const newFormData = {
        ...state.formData,
        coverTheCost: action.payload,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      return {
        ...state,
        formData: newFormData,
      };
    }

    case 'SET_GIFT': {
      const { amount, customAmount } = giftAdjustedFormFields(state, action.payload);
      const amounts = calculateDonationAmounts(amount, customAmount, state.formData.coverTheCost);
      const newFormData: FormData = {
        ...state.formData,
        gift: action.payload,
        noGift: false,
        amount,
        customAmount,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, state.formDetails),
          // Re-validate information step because shipping fields become required
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'SET_NO_GIFT': {
      const resetToBase = action.payload;
      const { amount, customAmount } = resetToBase
        ? preserveCurrentOrBaseFormFields(state)
        : { amount: state.formData.amount, customAmount: state.formData.customAmount };
      const amounts = calculateDonationAmounts(amount, customAmount, state.formData.coverTheCost);
      const newFormData: FormData = {
        ...state.formData,
        noGift: action.payload,
        gift: action.payload ? null : state.formData.gift,
        amount,
        customAmount,
        coverTheCostAmount: amounts.coverTheCostAmount,
        totalDonationAmount: amounts.totalDonationAmount,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          amount: validateAmountStep(newFormData),
          gift: validateGiftStep(newFormData, state.formDetails),
          // Re-validate information step because shipping fields are no longer required
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'SET_PAYMENT_METHOD': {
      const newFormData = {
        ...state.formData,
        paymentMethod: action.payload,
        // Clear card, bank, and Apple Pay data when changing payment method
        cardData: null,
        bankData: null,
        applePayData: null,
      };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          payment: validatePaymentStep(newFormData),
        },
      };
    }

    case 'SET_CARD_DATA': {
      const newFormData = {
        ...state.formData,
        cardData: action.payload,
      };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          payment: validatePaymentStep(newFormData),
        },
      };
    }

    case 'SET_BANK_DATA': {
      const newFormData = {
        ...state.formData,
        bankData: action.payload,
      };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          payment: validatePaymentStep(newFormData),
        },
      };
    }

    case 'SET_PAYPAL_APPROVAL': {
      if (!state.formDetails || state.formData.paymentMethod !== 'paypal') return state;
      const formData = { ...prefillPaypalDonor(state.formData, action.payload.donor, Boolean(state.memberId) || state.mode !== 'default'), paypalApproval: action.payload.approval };
      if (!hasPaypalApproval(formData, state.formDetails)) return state;
      return { ...state, formData, validation: { ...state.validation,
        payment: true,
        information: validateInformationStep(formData, true, { alwaysShowAddress: state.formDetails.always_show_address }),
      } };
    }
    case 'CLEAR_PAYPAL_APPROVAL':
      return { ...state, formData: { ...state.formData, paypalApproval: null }, validation: { ...state.validation, payment: false } };
    case 'SET_PAYPAL_UNCERTAIN':
      return { ...state, formData: { ...state.formData, paypalCompletionUncertain: true } };

    case 'SET_APPLE_PAY_DATA': {
      const newFormData = {
        ...state.formData,
        applePayData: action.payload,
      };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          payment: validatePaymentStep(newFormData),
        },
      };
    }

    case 'SET_CAN_MAKE_APPLE_PAY': {
      return {
        ...state,
        canMakeApplePay: action.payload,
      };
    }

    case 'SET_MEMBER_ID': {
      return {
        ...state,
        memberId: action.payload,
      };
    }

    case 'SET_DONOR_INFO': {
      const newFormData = {
        ...state.formData,
        ...action.payload,
      };
      const requiresEmail = state.formDetails?.kind !== 'call_center';
      const validationOptions = { disableCaptcha: state.disableCaptcha, alwaysShowAddress: state.formDetails?.always_show_address ?? false };
      return {
        ...state,
        formData: newFormData,
        validation: {
          ...state.validation,
          information: validateInformationStep(newFormData, requiresEmail, validationOptions),
        },
      };
    }

    case 'NEXT_STEP': {
      if (state.mode === 'billing-update') {
        const nextStep: FormStep =
          state.currentStep === 'amount' ? 'payment' : state.currentStep === 'payment' ? 'receipt' : 'receipt';
        return applyStepTransition(state, nextStep);
      }

      const hasGifts = (state.formDetails?.gifts.length ?? 0) > 0;
      const showMonthlyUpsell = shouldShowMonthlyUpsell(state);

      let nextStep: FormStep;

      switch (state.currentStep) {
        case 'amount':
          if (showMonthlyUpsell) {
            nextStep = 'monthly-upsell';
          } else {
            nextStep = hasGifts ? 'gift' : 'payment';
          }
          break;
        case 'monthly-upsell':
          nextStep = hasGifts ? 'gift' : 'payment';
          break;
        case 'gift':
          nextStep = 'payment';
          break;
        case 'payment':
          nextStep = 'information';
          break;
        case 'information':
          nextStep = 'receipt';
          break;
        case 'receipt':
        default:
          nextStep = 'receipt';
          break;
      }

      return applyStepTransition(state, nextStep);
    }

    case 'PREV_STEP': {
      if (state.mode === 'billing-update') {
        const prevStep: FormStep = state.currentStep === 'payment' ? 'amount' : 'amount';
        return applyStepTransition(state, prevStep);
      }

      const hasGifts = (state.formDetails?.gifts.length ?? 0) > 0;
      const showMonthlyUpsell = shouldShowMonthlyUpsell(state);

      let prevStep: FormStep;

      switch (state.currentStep) {
        case 'amount':
          prevStep = 'amount';
          break;
        case 'monthly-upsell':
          prevStep = 'amount';
          break;
        case 'gift':
          prevStep = showMonthlyUpsell ? 'monthly-upsell' : 'amount';
          break;
        case 'payment':
          if (hasGifts) {
            prevStep = 'gift';
          } else {
            prevStep = showMonthlyUpsell ? 'monthly-upsell' : 'amount';
          }
          break;
        case 'information':
          prevStep = 'payment';
          break;
        case 'receipt':
        default:
          prevStep = 'receipt';
          break;
      }

      return applyStepTransition(state, prevStep);
    }

    case 'SET_STEP':
      return applyStepTransition(state, action.payload);

    case 'SET_SUBMITTING':
      return {
        ...state,
        isSubmitting: action.payload,
      };

    case 'SET_RECEIPT':
      return {
        ...state,
        receipt: action.payload,
        isSubmitting: false,
        currentStep: 'receipt',
        submissionError: null,
      };

    case 'UPDATE_VALIDATION':
      return {
        ...state,
        validation: {
          ...state.validation,
          ...action.payload,
        },
      };

    case 'SET_UTM_CAMPAIGN':
      return {
        ...state,
        utmCampaign: action.payload,
      };

    default:
      return state;
  }
}


// Keep checkout approvals tied to the exact terms that PayPal approved. This
// wrapper catches amount reconciliation as well as direct form edits.
export function formReducer(state: DonationFormState, action: FormAction): DonationFormState {
  if (state.formData.paymentMethod === 'paypal'
    && (state.isSubmitting || state.formData.paypalCompletionUncertain)
    && ['SET_STEP', 'NEXT_STEP', 'PREV_STEP', 'SET_AMOUNT', 'SET_CUSTOM_AMOUNT', 'SET_FREQUENCY',
      'SET_GIFT', 'SET_NO_GIFT', 'SET_COVER_THE_COST', 'SET_PAYMENT_METHOD', 'SET_USE_NEW_PAYMENT_METHOD', 'SET_DONOR_INFO'].includes(action.type)) return state;
  let next = reduceForm(state, action);
  if (next.formData.paypalApproval && (next.formData.paymentMethod !== 'paypal'
    || !next.formDetails || !hasPaypalApproval(next.formData, next.formDetails))) {
    next = { ...next, formData: { ...next.formData, paypalApproval: null } };
  }
  if (next.formData.paymentMethod === 'paypal') {
    next = { ...next, validation: { ...next.validation,
      payment: Boolean(next.formDetails && hasPaypalApproval(next.formData, next.formDetails)),
    } };
    if (next.currentStep === 'information' && !next.validation.payment) next = { ...next, currentStep: 'payment' };
  }
  return next;
}
